import { useCallback, useMemo } from 'react';
import { FileItem, FileStatus } from '@sib/types';
import {
  useFileManagerFileUploadStore,
  useTicketFileUploadStore,
} from '@spendbrain/stores/fileUploadStore';
import { useUploadQueueGuardStore } from '../stores';
import { UploadQueueGuardDialogProps } from '../types/upload-queue-guard';

interface UploadQueueGuardRequest {
  hasActiveUploads: boolean;
  uploadingFilesCount: number;
  requestAction: (action: () => void) => void;
}

interface UploadQueueGuardDialogState {
  isDialogOpen: boolean;
  dialogProps: Omit<UploadQueueGuardDialogProps, 'onOpenFileManager'>;
}

const isUploading = (file: { status: FileStatus }) =>
  file.status === FileStatus.Uploading;

const isUploaded = (file: { status: FileStatus }) =>
  file.status === FileStatus.Uploaded;

const isError = (file: { status: FileStatus }) =>
  file.status === FileStatus.Error;

const getUploadStats = (files: FileItem[]) => ({
  total: files.length,
  uploading: files.filter(isUploading).length,
  uploaded: files.filter(isUploaded).length,
  error: files.filter(isError).length,
});

const useUploadQueueStats = () => {
  const fileManagerStore = useFileManagerFileUploadStore();
  const ticketStore = useTicketFileUploadStore();
  const fileManagerStats = useMemo(
    () => getUploadStats(fileManagerStore.files),
    [fileManagerStore.files]
  );
  const ticketStats = useMemo(
    () => getUploadStats(ticketStore.files),
    [ticketStore.files]
  );

  return {
    totalFilesCount: fileManagerStats.total + ticketStats.total,
    uploadingFilesCount: fileManagerStats.uploading + ticketStats.uploading,
    uploadedFilesCount: fileManagerStats.uploaded + ticketStats.uploaded,
    errorFilesCount: fileManagerStats.error + ticketStats.error,
  };
};

export const useUploadQueueGuard = (): UploadQueueGuardRequest => {
  const openDialog = useUploadQueueGuardStore(state => state.openDialog);
  const { totalFilesCount, uploadingFilesCount } = useUploadQueueStats();

  const hasActiveUploads = uploadingFilesCount > 0;

  const clearQueues = useCallback(() => {
    const fileManagerStore = useFileManagerFileUploadStore.getState();
    const ticketStore = useTicketFileUploadStore.getState();
    fileManagerStore.cancelAllUploads();
    fileManagerStore.resetFiles();
    ticketStore.cancelAllUploads();
    ticketStore.resetFiles();
  }, []);

  const executeAction = useCallback(
    (action: () => void) => {
      clearQueues();
      action();
    },
    [clearQueues]
  );

  const requestAction = useCallback(
    (action: () => void) => {
      if (!hasActiveUploads) return executeAction(action);

      openDialog(action, totalFilesCount);
    },
    [executeAction, hasActiveUploads, openDialog, totalFilesCount]
  );

  return {
    hasActiveUploads,
    uploadingFilesCount,
    requestAction,
  };
};

export const useUploadQueueGuardDialog = (): UploadQueueGuardDialogState => {
  const {
    isDialogOpen,
    pendingAction,
    totalFilesCountOnOpen,
    closeDialog,
    clearPendingAction,
    markExecuteOnClose,
  } = useUploadQueueGuardStore();
  const { totalFilesCount, uploadingFilesCount, errorFilesCount } =
    useUploadQueueStats();
  const displayTotalFilesCount = totalFilesCountOnOpen || totalFilesCount;

  const clearQueues = useCallback(() => {
    const fileManagerStore = useFileManagerFileUploadStore.getState();
    const ticketStore = useTicketFileUploadStore.getState();
    fileManagerStore.cancelAllUploads();
    fileManagerStore.resetFiles();
    ticketStore.cancelAllUploads();
    ticketStore.resetFiles();
  }, []);

  const executeAction = useCallback(
    (action: () => void) => {
      clearQueues();
      action();
    },
    [clearQueues]
  );

  const confirmAction = useCallback(() => {
    if (!pendingAction) return;

    markExecuteOnClose();
  }, [pendingAction, markExecuteOnClose]);

  const cancelAction = useCallback(() => {
    clearPendingAction();
    closeDialog();
  }, [clearPendingAction, closeDialog]);

  const handleDialogClose = useCallback(() => {
    const {
      pendingAction: action,
      shouldExecutePendingActionOnClose: shouldExecute,
    } = useUploadQueueGuardStore.getState();

    if (!action || !shouldExecute) return cancelAction();

    closeDialog();
    clearPendingAction();
    executeAction(action);
  }, [cancelAction, closeDialog, clearPendingAction, executeAction]);

  return {
    isDialogOpen,
    dialogProps: {
      open: isDialogOpen,
      displayTotalFilesCount,
      errorFilesCount,
      uploadingFilesCount,
      onClose: handleDialogClose,
      onConfirm: confirmAction,
    },
  };
};
